import getConfig from 'next/config'
import { sendEvent } from './coreAnalytics'
import { CarouselProduct } from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { buildGaPromoObject } from './analyticsUtils'
import { sendTealiumCarouselPromotionEvent } from '../tealiumAnalytics'

const {
  publicRuntimeConfig: { isTealiumEnabled },
} = getConfig()

// Field that must be attached to 'items' on the ecommerce portion of a GA4 Promo Event
type GA4ProductPromoItem = {
  creative_name?: string
  index: number
  item_brand?: string
  item_id?: string
  item_list_id: string
  item_list_name: string
  promotion_id: string
  promotion_name: string
}

/*
  Promo Item has normal GA4 event plus some extra creative slot fields.
  Some of these are Tealium specific.
  eCommerce object with special promo items is then attached to this.
*/
type GA4MasterPromoItem = {
  event?: 'view_promotion'
  ecommerce: {
    creative_slot: string
    items: GA4ProductPromoItem[]
  }
}

type GA4CarouselEventInputs = {
  creativeSlot: string
  pageType: string
  pageTypeDetails?: string | null
  products: CarouselProduct[]
}

type Ga4CarouselProductClickInputs = {
  product: CarouselProduct
  indexPosition?: number
  creativeSlot: string
  pageType: string
  pageTypeDetail?: string
}

export const sendCarouselPromoEvent = ({
  creativeSlot,
  pageType,
  pageTypeDetails,
  products,
}: GA4CarouselEventInputs) => {
  const listingPageDetails = pageTypeDetails || pageType

  const promotedItems: GA4ProductPromoItem[] = products.map(
    (product, index) => {
      // On sliced in view arrays we need to give the analytics calculated position (basically original index value + 1). Set that when slicing.
      // In cases where we have all the data and don't supply the analyticsPosition just use the index + 1 to get the human position.
      const viewableIndex = product.analyticsPosition || index + 1

      return buildGaPromoObject(
        product?.gaProductCode,
        product,
        viewableIndex,
        pageType,
        listingPageDetails
      )
    }
  )

  const builtPromoItem: GA4MasterPromoItem = {
    event: 'view_promotion',
    ecommerce: {
      creative_slot: creativeSlot.toLowerCase(),
      items: promotedItems,
    },
  }

  sendEvent({ payload: builtPromoItem })

  // Send carousel view for Tealium when it is enabled.
  if (isTealiumEnabled) {
    sendTealiumCarouselPromotionEvent(
      'view_promotion',
      products,
      creativeSlot,
      pageType,
      pageTypeDetails
    )
  }
}

export const sendCarouselItemClickEvent = ({
  product,
  indexPosition = 0,
  creativeSlot,
  pageType,
  pageTypeDetail,
}: Ga4CarouselProductClickInputs) => {
  const viewableIndex = indexPosition + 1
  const finalPageDetail = pageTypeDetail || pageType

  const builtPromoObj = buildGaPromoObject(
    product?.gaProductCode,
    product,
    viewableIndex,
    pageType,
    finalPageDetail
  )

  sendEvent({
    payload: {
      event: 'select_promotion',
      ecommerce: {
        creative_slot: creativeSlot.toLowerCase(),
        items: [builtPromoObj],
      },
    },
  })

  // Send carousel view for Tealium when it is enabled.
  if (isTealiumEnabled) {
    sendTealiumCarouselPromotionEvent(
      'select_promotion',
      [product],
      creativeSlot,
      pageType,
      pageTypeDetail
    )
  }
}
